import { IconBell } from 'featherico';
import { partition } from 'lodash/fp';

import { useWarningsMuter } from '../../hooks/useWarningMuter';
import type { COLOURS } from '../AlertBox';
import AlertBox from '../AlertBox';
import Button from '../Button';
import styles from './styles.m.less';

type UiWarningProps = {
    warningId: string;
    show: boolean;
    title: string;
    children: React.ReactNode;
    colour: keyof typeof COLOURS;
    rightSlot?: React.ReactNode; // optional existing element that Ui warning wraps (like "Create Cycle" in objective cycle creation)
};

export function UiWarning({ warningId, show, title, children, colour, rightSlot }: UiWarningProps) {
    const { isMuted, mute, unmute } = useWarningsMuter();

    if (!show) return null;

    return (
        <>
            <div className={styles.buttonContainer}>
                {isMuted(warningId) && (
                    <Button onClick={() => unmute(warningId)} kind="PLAIN" className={styles.bellIcon}>
                        <IconBell /> 1
                    </Button>
                )}
                {rightSlot}
            </div>

            {!isMuted(warningId) && (
                <AlertBox colour={colour} title={title} onDismiss={() => mute(warningId)}>
                    {children}
                </AlertBox>
            )}
        </>
    );
}

type Warning = {
    id: string;
    title: string;
    content: React.ReactNode;
};

type MultipleUiWarningProps = {
    warnings: Warning[];
    colour: keyof typeof COLOURS;
    expirationDays?: number | null;
};

/**
 * Like UiWarning above, but collapses any number of warnings into a single bell,
 * so dismissing several of them doesn't leave a row of bell icons behind.
 */
export function MultipleUiWarning({ warnings, colour, expirationDays }: MultipleUiWarningProps) {
    const { isMuted, mute, unmuteMany } = useWarningsMuter({ expirationDays });

    const [muted, shown] = partition(warning => isMuted(warning.id), warnings);

    return (
        <>
            {muted.length > 0 && (
                <div className={styles.bellContainer}>
                    <Button
                        onClick={() => unmuteMany(muted.map(warning => warning.id))}
                        kind="PLAIN"
                        className={styles.bellIcon}
                    >
                        <IconBell /> {muted.length}
                    </Button>
                </div>
            )}

            {shown.map(warning => (
                <AlertBox key={warning.id} colour={colour} title={warning.title} onDismiss={() => mute(warning.id)}>
                    {warning.content}
                </AlertBox>
            ))}
        </>
    );
}
